import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, DollarSign, Building2, Calendar, Banknote, Smartphone, CreditCard, FileText, CheckCircle, AlertCircle, User, Check, ChevronRight, IndianRupee, Clock, Search, Phone, Mail } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { partiesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash', icon: Banknote, color: 'emerald' },
  { value: 'upi', label: 'UPI', icon: Smartphone, color: 'teal' },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2, color: 'blue' },
  { value: 'card', label: 'Card', icon: CreditCard, color: 'amber' },
  { value: 'cheque', label: 'Cheque', icon: FileText, color: 'slate' },
  { value: 'other', label: 'Other', icon: DollarSign, color: 'gray' },
];

const PAID_FROM_OPTIONS = [
  { value: 'Cash', label: 'Cash Account' },
  { value: 'Bank Account', label: 'Bank Account' },
  { value: 'Petty Cash', label: 'Petty Cash' },
];

const BILL_STATUS_CONFIG = {
  unpaid: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Unpaid' },
  partially_paid: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Partially Paid' },
  paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
  overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
};

const RecordPaymentMadePage = () => {
  const { businessId, id: paymentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;
  const isEdit = !!paymentId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [openBills, setOpenBills] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [supplierStats, setSupplierStats] = useState({ total_due: 0, overdue_amount: 0, bill_count: 0 });
  const [staff, setStaff] = useState([]);

  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    supplier_phone: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount_paid: 0,
    payment_mode: 'cash',
    transaction_reference: '',
    debit_from_account: 'Cash',
    paid_by_user_id: '',
    paid_by_name: '',
    notes: '',
    allocations: [],
  });

  const [selectedBill, setSelectedBill] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    fetchSuppliers();
    fetchStaff();
    if (isEdit) fetchPayment();
    
    const supplierId = searchParams.get('supplier_id');
    const invoiceId = searchParams.get('invoice_id');
    
    if (supplierId) {
      // Pre-fill supplier and fetch bills
      prefillFromParams(supplierId, invoiceId);
    }
  }, [isEdit]);

  const prefillFromParams = async (supplierId, invoiceId) => {
    try {
      // If invoice_id is provided, fetch that specific invoice first and get supplier from it
      if (invoiceId) {
        const invoiceRes = await api.get(`/purchase-invoices/${invoiceId}`);
        {
          const invoice = invoiceRes.data;
          const balanceDue = invoice.balance_due ?? (invoice.grand_total - (invoice.amount_paid || 0)) ?? invoice.grand_total ?? 0;
          
          // Set supplier from invoice data
          setFormData(prev => ({
            ...prev,
            supplier_id: invoice.supplier_id || supplierId,
            supplier_name: invoice.supplier_name || '',
            supplier_phone: invoice.supplier_phone || '',
            amount_paid: balanceDue,
            allocations: [{
              invoice_id: invoice.id,
              invoice_number: invoice.invoice_number,
              invoice_total: invoice.grand_total,
              balance_before: balanceDue,
              allocated_amount: balanceDue
            }]
          }));
          setSupplierSearch(invoice.supplier_name || '');
          setSelectedBill({
            ...invoice,
            balance_due: balanceDue
          });
          setOpenBills([{
            ...invoice,
            balance_due: balanceDue
          }]);
          setSupplierStats({ 
            total_due: balanceDue, 
            overdue_amount: invoice.status === 'overdue' ? balanceDue : 0, 
            bill_count: 1 
          });
          setCurrentStep(3);
          return;
        }
      }
      
      // Fallback: Try to get supplier info from suppliers list
      const suppliersRes = await partiesAPI.list({ type: 'supplier' });
      {
        const data = suppliersRes.data;
        const suppliersList = data.parties || data || [];
        const supplier = suppliersList.find(s => s.id === supplierId);
        
        if (supplier) {
          setFormData(prev => ({
            ...prev,
            supplier_id: supplier.id,
            supplier_name: supplier.name,
            supplier_phone: supplier.phone || ''
          }));
          setSupplierSearch(supplier.name);
          setCurrentStep(2);
          
          // Fetch open bills for this supplier
          await fetchBillsForSupplier(supplierId, invoiceId);
        }
      }
    } catch (e) { 
      toast.error(getApiErrorMessage(e, 'Error loading supplier data'));
    }
  };
  
  const fetchBillsForSupplier = async (supplierId, targetInvoiceId = null) => {
    try {
      const billsRes = await api.get('/purchase-invoices/open-invoices/' + supplierId);
      {
        const data = billsRes.data;
        const bills = data.invoices || data || [];
        setOpenBills(bills);
        
        const totalDue = bills.reduce((sum, b) => sum + (b.balance_due || 0), 0);
        const overdue = bills.filter(b => b.status === 'overdue').reduce((sum, b) => sum + (b.balance_due || 0), 0);
        setSupplierStats({ total_due: totalDue, overdue_amount: overdue, bill_count: bills.length });
        
        // If targetInvoiceId provided, auto-select that bill
        if (targetInvoiceId) {
          const targetBill = bills.find(b => b.id === targetInvoiceId);
          if (targetBill) {
            setSelectedBill(targetBill);
            setFormData(prev => ({
              ...prev,
              amount_paid: targetBill.balance_due || 0,
              allocations: [{
                invoice_id: targetBill.id,
                invoice_number: targetBill.invoice_number,
                invoice_total: targetBill.grand_total,
                balance_before: targetBill.balance_due,
                allocated_amount: targetBill.balance_due
              }]
            }));
            setCurrentStep(3);
          }
        }
      }
    } catch (e) { 
      toast.error(getApiErrorMessage(e, 'Failed to load bills'));
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await partiesAPI.list({ type: 'supplier' });
      const data = res.data;
      setSuppliers(data.parties || data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load data')); }
  };

  const fetchStaff = async () => {
    try {
      const res = await api.get('/settings/users/field-staff');
      setStaff(res.data || []);
    } catch (e) {
      // Staff list is optional -- don't block the page
      setStaff([]);
    }
  };

  const fetchOpenBills = async (supplierId) => {
    try {
      const res = await api.get('/purchase-invoices/open-invoices/' + supplierId);
      const data = res.data;
      const bills = data.invoices || data || [];
      setOpenBills(bills);

      const totalDue = bills.reduce((sum, b) => sum + (b.balance_due || 0), 0);
      const overdue = bills.filter(b => b.status === 'overdue').reduce((sum, b) => sum + (b.balance_due || 0), 0);
      setSupplierStats({ total_due: totalDue, overdue_amount: overdue, bill_count: bills.length });
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load data')); }
  };

  const fetchPayment = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payments-made/${paymentId}`);
      const data = res.data;
      setFormData({ ...data });
      setSupplierSearch(data.supplier_name || '');
      if (data.supplier_id) fetchOpenBills(data.supplier_id);
      setCurrentStep(4);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load data')); }
    setLoading(false);
  };

  const selectSupplier = (supplier) => {
    setFormData(prev => ({
      ...prev,
      supplier_id: supplier.id,
      supplier_name: supplier.name,
      supplier_phone: supplier.phone || ''
    }));
    setSupplierSearch(supplier.name);
    setShowSupplierDropdown(false);
    fetchOpenBills(supplier.id);
    setCurrentStep(2);
  };

  const selectBill = (bill) => {
    setSelectedBill(bill);
    setFormData(prev => ({
      ...prev,
      amount_paid: bill.balance_due || 0,
      allocations: [{
        invoice_id: bill.id,
        invoice_number: bill.invoice_number,
        invoice_total: bill.grand_total,
        balance_before: bill.balance_due,
        allocated_amount: bill.balance_due
      }]
    }));
    setCurrentStep(3);
  };

  const selectAdvancePayment = () => {
    setSelectedBill(null);
    setFormData(prev => ({ ...prev, amount_paid: 0, allocations: [] }));
    setCurrentStep(3);
  };

  const handleSubmit = async () => {
    if (!formData.supplier_id) { toast.error('Please select a supplier'); return; }
    if (formData.amount_paid <= 0) { toast.error('Please enter payment amount'); return; }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/payments-made/${paymentId}`, formData);
      } else {
        await api.post('/payments-made', formData);
      }
      toast.success(isEdit ? 'Payment updated' : 'Payment recorded');
      navigate(`/business/${bid}/payments-made`);
    } catch (e) {
      const err = e.response?.data;
      toast.error(getApiErrorMessage(err, 'Error saving payment'));
    }
    setSaving(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filteredSuppliers = suppliers.filter(s => s.name?.toLowerCase().includes(supplierSearch.toLowerCase()));

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading payment...</p>
        </div>
      </div>
    );
  }

  const steps = [
    { num: 1, label: 'Select Supplier' },
    { num: 2, label: 'Select Bill' },
    { num: 3, label: 'Pay Now' },
    { num: 4, label: 'Details' }
  ];

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[20px] font-bold text-slate-800">{isEdit ? 'Edit Payment' : 'Record Payment'}</h1>
                <p className="text-[13px] text-slate-500 mt-0.5">Pay supplier bill</p>
              </div>
            </div>

            {/* Steps Indicator */}
            <div className="flex items-center gap-2">
              {steps.map((step, idx) => (
                <React.Fragment key={step.num}>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-medium ${currentStep >= step.num ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${currentStep >= step.num ? 'bg-teal-600 text-white' : 'bg-slate-300 text-white'}`}>
                      {currentStep > step.num ? <Check className="w-3 h-3" /> : step.num}
                    </span>
                    {step.label}
                  </div>
                  {idx < steps.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300" />}
                </React.Fragment>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-all duration-200 text-[13px] font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || currentStep < 3}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT - Premium 3-Column Layout */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Supplier Selection (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Supplier Search Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Select Supplier</h2>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search supplier by name..."
                    value={supplierSearch}
                    onChange={(e) => { setSupplierSearch(e.target.value); setShowSupplierDropdown(true); }}
                    onFocus={() => setShowSupplierDropdown(true)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                  />
                  {showSupplierDropdown && filteredSuppliers.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {filteredSuppliers.map(s => (
                        <div key={s.id} onClick={() => selectSupplier(s)} className="px-4 py-3 hover:bg-teal-50 cursor-pointer text-[13px] flex items-center gap-3 border-b border-slate-100 last:border-0">
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                            <span className="text-[12px] font-bold text-teal-700">{s.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{s.name}</p>
                            {s.phone && <p className="text-[11px] text-slate-500">{s.phone}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {formData.supplier_id && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-teal-700">{formData.supplier_name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{formData.supplier_name}</p>
                        <p className="text-[12px] text-slate-500">Selected Supplier</p>
                      </div>
                    </div>
                    {formData.supplier_phone && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {formData.supplier_phone}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Supplier Stats */}
              {formData.supplier_id && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                      <AlertCircle className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Outstanding</h2>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Total Due</span>
                      <span className="text-[16px] font-bold text-amber-600">₹{supplierStats.total_due.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Open Bills</span>
                      <span className="text-[13px] font-medium text-slate-700">{supplierStats.bill_count}</span>
                    </div>
                    {supplierStats.overdue_amount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-[12px] text-red-500">Overdue</span>
                        <span className="text-[13px] font-medium text-red-600">₹{supplierStats.overdue_amount.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Details Card */}
              {currentStep >= 3 && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Payment Details</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-1">Payment Date</label>
                      <input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-1">Reference #</label>
                      <input
                        type="text"
                        placeholder="Transaction ref..."
                        value={formData.transaction_reference}
                        onChange={(e) => setFormData(prev => ({ ...prev, transaction_reference: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-1">Paid From</label>
                      <select
                        value={formData.debit_from_account}
                        onChange={(e) => setFormData(prev => ({ ...prev, debit_from_account: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      >
                        {PAID_FROM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-1">Paid By</label>
                      <select
                        value={formData.paid_by_user_id}
                        onChange={(e) => {
                          const user = staff.find(s => s.id === e.target.value);
                          setFormData(prev => ({ ...prev, paid_by_user_id: e.target.value, paid_by_name: user?.full_name || user?.name || '' }));
                        }}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      >
                        <option value="">Select staff...</option>
                        {staff.map(s => <option key={s.id} value={s.id}>{s.full_name || s.name || 'Unnamed'}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-1">Notes</label>
                      <textarea
                        placeholder="Payment notes..."
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CENTER COLUMN - Bills / Payment Mode (6 cols) */}
            <div className="col-span-12 lg:col-span-6 space-y-4">
              
              {/* Bills Selection */}
              {currentStep >= 2 && formData.supplier_id && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                        <FileText className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h2 className="text-[14px] font-semibold text-slate-800">Select Bill to Pay</h2>
                        <p className="text-[12px] text-slate-500">{openBills.length} open bill(s)</p>
                      </div>
                    </div>
                    <button
                      onClick={selectAdvancePayment}
                      className="inline-flex items-center gap-2 px-3 py-2 text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 text-[12px] font-medium transition-colors"
                    >
                      <DollarSign className="w-4 h-4" />
                      Pay Advance
                    </button>
                  </div>
                  
                  {openBills.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Bill #</th>
                            <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                            <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Total</th>
                            <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Due</th>
                            <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                            <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {openBills.map((bill) => {
                            const statusConfig = BILL_STATUS_CONFIG[bill.status] || BILL_STATUS_CONFIG.unpaid;
                            const isSelected = selectedBill?.id === bill.id;
                            return (
                              <tr key={bill.id} className={`hover:bg-slate-50/50 transition-colors duration-150 ${isSelected ? 'bg-teal-50/50' : ''}`}>
                                <td className="px-5 py-4">
                                  <p className="text-[14px] font-medium text-slate-800">{bill.invoice_number}</p>
                                  {bill.supplier_invoice_no && <p className="text-[11px] text-slate-400">{bill.supplier_invoice_no}</p>}
                                </td>
                                <td className="px-5 py-4 text-[13px] text-slate-600">{formatDate(bill.invoice_date)}</td>
                                <td className="px-5 py-4 text-right text-[13px] text-slate-700">₹{(bill.grand_total || 0).toLocaleString('en-IN')}</td>
                                <td className="px-5 py-4 text-right">
                                  <span className="text-[14px] font-bold text-amber-600">₹{(bill.balance_due || 0).toLocaleString('en-IN')}</span>
                                </td>
                                <td className="px-5 py-4 text-center">
                                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                                    {statusConfig.label}
                                  </span>
                                </td>
                                <td className="px-5 py-4 text-center">
                                  <button
                                    onClick={() => selectBill(bill)}
                                    className={`px-4 py-2 rounded-lg text-[12px] font-medium transition-colors ${isSelected ? 'bg-teal-600 text-white' : 'bg-teal-100 text-teal-700 hover:bg-teal-200'}`}
                                  >
                                    {isSelected ? <Check className="w-4 h-4 mx-auto" /> : 'Select'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-[14px] text-slate-500">No open bills for this supplier</p>
                      <button onClick={selectAdvancePayment} className="mt-4 text-teal-600 hover:underline text-[13px] font-medium">
                        Make Advance Payment
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Mode Selection */}
              {currentStep >= 3 && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                        <Banknote className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h2 className="text-[14px] font-semibold text-slate-800">Payment Mode</h2>
                        <p className="text-[12px] text-slate-500">Select how you are paying</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <div className="grid grid-cols-3 gap-3">
                      {PAYMENT_MODES.map((mode) => {
                        const ModeIcon = mode.icon;
                        const isSelected = formData.payment_mode === mode.value;
                        return (
                          <button
                            key={mode.value}
                            onClick={() => setFormData(prev => ({ ...prev, payment_mode: mode.value }))}
                            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 ${isSelected ? `border-teal-500 bg-teal-50` : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected ? 'bg-teal-500' : 'bg-slate-100'}`}>
                              <ModeIcon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                            </div>
                            <span className={`text-[12px] font-medium ${isSelected ? 'text-teal-700' : 'text-slate-600'}`}>{mode.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Payment Amount */}
                    <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                      <label className="text-[12px] text-slate-500 block mb-2">Payment Amount *</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[18px] font-medium text-slate-400">₹</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.amount_paid}
                          onChange={(e) => setFormData(prev => ({ ...prev, amount_paid: parseFloat(e.target.value) || 0 }))}
                          className="w-full pl-10 pr-4 py-4 bg-white border border-slate-200 rounded-xl text-[24px] font-bold text-teal-600 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                        />
                      </div>
                      {selectedBill && (
                        <div className="flex justify-between items-center mt-3 text-[12px]">
                          <span className="text-slate-500">Bill Balance: ₹{selectedBill.balance_due?.toLocaleString('en-IN')}</span>
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, amount_paid: selectedBill.balance_due }))}
                            className="text-teal-600 hover:underline font-medium"
                          >
                            Pay Full Amount
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Payment Summary */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-5 shadow-sm border border-teal-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Payment Summary</h2>
                </div>
                
                <div className="space-y-3">
                  {selectedBill && (
                    <>
                      <div className="flex justify-between text-[13px]">
                        <span className="text-slate-600">Bill Amount</span>
                        <span className="text-slate-800 font-medium">₹{(selectedBill.grand_total || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-[13px]">
                        <span className="text-slate-600">Already Paid</span>
                        <span className="text-emerald-600 font-medium">₹{((selectedBill.grand_total || 0) - (selectedBill.balance_due || 0)).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-[13px]">
                        <span className="text-slate-600">Balance Due</span>
                        <span className="text-amber-600 font-medium">₹{(selectedBill.balance_due || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </>
                  )}
                  
                  <div className="border-t border-teal-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Payment Amount</span>
                      <span className="text-[22px] font-bold text-teal-600">
                        ₹{(formData.amount_paid || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    
                    {selectedBill && formData.amount_paid > 0 && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-teal-200">
                        <p className="text-[12px] text-slate-500">After Payment</p>
                        <p className="text-[16px] font-bold text-slate-800">
                          ₹{Math.max(0, (selectedBill.balance_due || 0) - formData.amount_paid).toLocaleString('en-IN')}
                          <span className="text-[12px] font-normal text-slate-500 ml-1">remaining</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Payment Info</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Type</span>
                    <span className="text-[13px] font-medium text-slate-700">{selectedBill ? 'Bill Payment' : 'Advance'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Mode</span>
                    <span className="text-[13px] font-medium text-slate-700 capitalize">{formData.payment_mode}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(formData.payment_date)}</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  <button
                    onClick={handleSubmit}
                    disabled={saving || currentStep < 3 || formData.amount_paid <= 0}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-teal-50 hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <CheckCircle className="w-4 h-4 text-teal-600" />
                    <span className="text-[13px] font-medium text-teal-700">Record Payment</span>
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200"
                  >
                    <ArrowLeft className="w-4 h-4 text-slate-600" />
                    <span className="text-[13px] font-medium text-slate-700">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordPaymentMadePage;
